/**
 * REGISTA AI: Analytics Utilities
 * 
 * Comprehensive tracking for conversion funnel optimization
 */

declare global {
  interface Window {
    plausible?: (event: string, options?: { props?: Record<string, string | number | boolean> }) => void;
  }
}

// Main analytics tracking function
export function trackEvent(
  event: string, 
  props?: Record<string, string | number | boolean>
) {
  if (typeof window !== 'undefined' && window.plausible) {
    window.plausible(event, { props });
  }
  
  // Also log to console in development
  if (process.env.NODE_ENV === 'development') {
    console.log('📊 Analytics Event:', event, props);
  }
}

// Conversion funnel events
export const analytics = {
  // Page views and navigation
  pageView: (page: string) => {
    trackEvent('Page View', { page });
  },

  // Hero section interactions
  heroCtaClick: (ctaType: 'waitlist' | 'demo') => {
    trackEvent('Hero CTA Click', { ctaType });
  },

  // Features section engagement
  featureView: (feature: string) => {
    trackEvent('Feature View', { feature });
  },

  // Cost savings section
  costSavingsView: () => {
    trackEvent('Cost Savings View');
  },

  // Demo interactions
  demoStart: (demoType: 'storyboard' | 'scene' | 'voice') => {
    trackEvent('Demo Start', { demoType });
  },

  demoSuccess: (demoType: string, metadata?: Record<string, any>) => {
    trackEvent('Demo Success', { demoType, ...metadata });
  },

  demoError: (demoType: string, error: string) => {
    trackEvent('Demo Error', { demoType, error });
  },

  // Waitlist funnel
  waitlistFormStart: (source: string) => {
    trackEvent('Waitlist Form Start', { source });
  },

  waitlistFormSubmit: (source: string, hasOptionalFields: boolean) => {
    trackEvent('Waitlist Form Submit', { source, hasOptionalFields });
  },

  waitlistSuccess: (position: number | string, source: string, metadata?: Record<string, any>) => {
    trackEvent('Waitlist Success', { position, source, ...metadata });
  },

  waitlistError: (error: string, source: string) => {
    trackEvent('Waitlist Error', { error, source });
  },

  // Pricing section
  pricingView: () => {
    trackEvent('Pricing View');
  },

  pricingPlanClick: (plan: string) => {
    trackEvent('Pricing Plan Click', { plan });
  },

  // Templates section
  templateView: (template: string) => {
    trackEvent('Template View', { template });
  },

  templateClick: (template: string) => {
    trackEvent('Template Click', { template });
  },

  // Testimonials
  testimonialsView: () => {
    trackEvent('Testimonials View');
  },

  // Contact/Sales
  contactClick: (source: string) => {
    trackEvent('Contact Click', { source });
  },

  // Workers AI specific events
  workersAiInteraction: (endpoint: string, success: boolean, responseTime?: number) => {
    trackEvent('Workers AI Interaction', { 
      endpoint, 
      success, 
      responseTime: responseTime || 0 
    });
  },

  // Performance tracking
  pageLoadTime: (time: number) => {
    trackEvent('Page Load Time', { time });
  },

  // User engagement
  scrollDepth: (depth: number) => {
    trackEvent('Scroll Depth', { depth });
  },

  timeOnPage: (seconds: number) => {
    trackEvent('Time on Page', { seconds });
  }
};

// Automatic scroll depth tracking
export function initScrollTracking() {
  if (typeof window === 'undefined') return;

  let maxScroll = 0;
  const thresholds = [25, 50, 75, 90, 100];
  const triggered = new Set<number>();

  const handleScroll = () => {
    const scrollPercent = Math.round(
      (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100
    );
    
    if (scrollPercent > maxScroll) {
      maxScroll = scrollPercent;
      
      thresholds.forEach(threshold => {
        if (scrollPercent >= threshold && !triggered.has(threshold)) {
          triggered.add(threshold);
          analytics.scrollDepth(threshold);
        }
      });
    }
  };

  window.addEventListener('scroll', handleScroll, { passive: true });
  
  return () => window.removeEventListener('scroll', handleScroll);
}

// Automatic time on page tracking
export function initTimeTracking() {
  if (typeof window === 'undefined') return;

  const startTime = Date.now();
  const intervals = [30, 60, 120, 300]; // 30s, 1m, 2m, 5m
  const triggered = new Set<number>();

  const checkTime = () => {
    const timeOnPage = Math.round((Date.now() - startTime) / 1000);
    
    intervals.forEach(interval => {
      if (timeOnPage >= interval && !triggered.has(interval)) {
        triggered.add(interval);
        analytics.timeOnPage(interval);
      }
    });
  };

  const timer = setInterval(checkTime, 10000); // Check every 10 seconds
  
  return () => clearInterval(timer);
}

// Page performance tracking
export function trackPageLoad() {
  if (typeof window === 'undefined') return;

  window.addEventListener('load', () => {
    // Track total page load time
    const loadTime = performance.timing.loadEventEnd - performance.timing.navigationStart;
    analytics.pageLoadTime(Math.round(loadTime));
  });
}

// Initialize all automatic tracking
export function initAnalytics() {
  if (typeof window === 'undefined') return;

  const cleanupFunctions = [
    initScrollTracking(),
    initTimeTracking()
  ].filter(Boolean);

  trackPageLoad();
  analytics.pageView('home');

  return () => {
    cleanupFunctions.forEach(cleanup => cleanup?.());
  };
}